/**
 * A deadline, ticking down to whole seconds, at ≤ 1 render per second while it
 * runs and 0 while it is not armed — the SAME shape `rail/live/RoundChip/
 * model.ts` already has (E2), extracted so the Details tab's status line (G3)
 * and the idle Stage (G5) can each get it without copying the tick logic a
 * third time. `RoundChip` itself is left untouched — its own test already pins
 * it, and this file changes nothing about it.
 */
import {useEffect, useRef, useState} from 'react';

/** the countdown is whole seconds, so a 250 ms tick cannot miss one */
const TICK_MS = 250;

/** whole seconds left, floored at 0. */
export const secondsLeft = (deadline: number | null, now: number): number =>
  deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));

/** `null` while nothing is armed; whole seconds left otherwise. */
export function useCountdownSeconds(deadline: number | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  /** the second currently on screen, so a tick can tell whether it moved */
  const shown = useRef(0);
  shown.current = secondsLeft(deadline, now);

  useEffect(() => {
    if (deadline === null) {
      return undefined;
    }
    const interval = setInterval(() => {
      const next = Date.now();
      if (secondsLeft(deadline, next) !== shown.current) {
        setNow(next);
      }
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [deadline]);

  return deadline === null ? null : secondsLeft(deadline, now);
}

export default useCountdownSeconds;
